/* ============================================================
   MTi PWA — app-native product screen. INSTALLED APP ONLY.

   Gated on html.pwa-standalone, so the public site never sees any of this.
   Everything here is additive chrome: we do not re-implement variant logic.

   LOAD ORDER IS LOAD-BEARING. This file is a later <script defer> than
   main-product-v2.js in the same section, so it runs after it (deferred
   scripts execute in document order). That matters because the v2 picker
   captures direct element references to its [data-pv2-opt] groups at init
   (optGroups is a snapshot array, and valuesAt/updateDisplay query off the
   stored group element). So we can relocate those groups into a bottom sheet
   afterwards and its gating, cascade-reconcile and price sync keep working
   untouched — the picker does not care where in the DOM its groups live.

   What this does:
     1. Options    → summary rows in the buy box + a real bottom sheet
     2. Detail     → segmented tab strip instead of a 15-section scroll
     3. Cross-sell → "Frequently bought together" lifts up under the buy
                     block; "Similar products" stays down in Overview
     4. Guest      → the trade-pricing CTA opens the existing pwa-auth-sheet
   ============================================================ */
(function () {
  'use strict';

  if (!document.documentElement.classList.contains('pwa-standalone')) return;

  var buy = document.querySelector('[data-pv2-buy]');
  if (!buy) return;

  /* Idempotency guard — same rationale as main-product-v2.js: theme-editor
     reloads and section re-renders can run this twice. */
  if (buy.hasAttribute('data-pwa-app-pdp')) return;
  buy.setAttribute('data-pwa-app-pdp', '');

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }

  var CHEV =
    '<svg class="pwaopt__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M9 6l6 6-6 6"/></svg>';

  /* ============================================================
     1. OPTIONS — summary rows + bottom sheet
     ============================================================ */

  var optsRoot = buy.querySelector('[data-pv2-opts]');
  var groups = optsRoot ? $$('[data-pv2-opt]', optsRoot) : [];
  var sheet, sheetTitle, sheetHint, sheetBody, rows = [], panes = [], lastFocus = null;

  function currentValue(g) {
    var c = g.querySelector('[data-opt-current]');
    return c ? c.textContent.trim() : '';
  }

  /* Why a value can't be picked, derived rather than authored: any disabled
     value at position > 0 is blocked by the choices before it (that IS the
     picker's cascade). Position 0 disabled means no SKU carries it at all. */
  function reasonFor(pos) {
    if (pos <= 0) return 'Not carried.';
    var prior = groups.slice(0, pos).map(currentValue).filter(Boolean);
    return prior.length ? 'Not carried with ' + prior.join(' / ') + '.' : 'Not carried.';
  }

  function buildSheet() {
    sheet = document.createElement('div');
    sheet.className = 'pwaps';
    sheet.hidden = true;

    var scrim = document.createElement('div');
    scrim.className = 'pwaps__scrim';
    scrim.addEventListener('click', closeSheet);

    var panel = document.createElement('div');
    panel.className = 'pwaps__panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    var grip = document.createElement('div');
    grip.className = 'pwaps__grip';

    var head = document.createElement('div');
    head.className = 'pwaps__head';
    sheetTitle = document.createElement('h3');
    sheetTitle.className = 'pwaps__title';
    sheetHint = document.createElement('span');
    sheetHint.className = 'pwaps__hint';
    head.appendChild(sheetTitle);
    head.appendChild(sheetHint);

    sheetBody = document.createElement('div');
    sheetBody.className = 'pwaps__body';

    panel.appendChild(grip);
    panel.appendChild(head);
    panel.appendChild(sheetBody);
    sheet.appendChild(scrim);
    sheet.appendChild(panel);
    document.body.appendChild(sheet);
  }

  function openSheet(i) {
    lastFocus = document.activeElement;
    sheetTitle.textContent = 'Choose ' + (groups[i].getAttribute('data-opt-name') || 'an option').toLowerCase();

    var vals = $$('[data-opt-value]', groups[i]);
    var live = vals.filter(function (b) { return !b.hasAttribute('disabled'); });
    sheetHint.textContent = live.length === vals.length
      ? vals.length + ' options'
      : live.length + ' of ' + vals.length + ' available';

    /* Re-stamp the blocked reason each open: the cascade means WHY a value is
       blocked changes as earlier options change. */
    vals.forEach(function (b) {
      var why = b.querySelector('[data-pwa-why]');
      if (b.hasAttribute('disabled')) {
        if (!why) {
          why = document.createElement('span');
          why.className = 'pv2-buy__size-why';
          why.setAttribute('data-pwa-why', '');
          b.appendChild(why);
        }
        why.textContent = reasonFor(i);
      } else if (why) {
        why.remove();
      }
    });

    panes.forEach(function (p, n) { p.hidden = n !== i; });
    sheet.hidden = false;
    requestAnimationFrame(function () { sheet.classList.add('is-open'); });
    var first = live[0] || panes[i];
    if (first && first.focus) first.focus();
  }

  function closeSheet() {
    if (!sheet || sheet.hidden) return;
    sheet.classList.remove('is-open');
    setTimeout(function () { sheet.hidden = true; }, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function syncRows() {
    groups.forEach(function (g, i) {
      var val = rows[i].querySelector('.pwaopt__val');
      if (val) val.textContent = currentValue(g);
    });
  }

  function buildOptions() {
    if (!optsRoot || !groups.length) return;
    buildSheet();

    var list = document.createElement('div');
    list.className = 'pwaopt';

    groups.forEach(function (g, i) {
      var row = document.createElement('button');
      row.type = 'button';
      row.className = 'pwaopt__row';
      row.innerHTML =
        '<span class="pwaopt__label"></span><span class="pwaopt__val"></span>' + CHEV;
      row.querySelector('.pwaopt__label').textContent = g.getAttribute('data-opt-name') || 'Option';
      row.addEventListener('click', function () { openSheet(i); });
      list.appendChild(row);
      rows.push(row);

      /* Move the real group into the sheet. The picker holds a reference to
         it, so all of its behaviour travels with it. */
      var pane = document.createElement('div');
      pane.className = 'pwaps__pane';
      pane.hidden = true;
      pane.appendChild(g);
      sheetBody.appendChild(pane);
      panes.push(pane);
    });

    optsRoot.parentNode.insertBefore(list, optsRoot);
    optsRoot.hidden = true;

    /* Picking a value closes the sheet. Deferred a tick so the picker's own
       click handler runs first and the row reads the settled value. */
    sheetBody.addEventListener('click', function (e) {
      var b = e.target.closest('[data-opt-value]');
      if (!b || b.hasAttribute('disabled')) return;
      setTimeout(function () { syncRows(); closeSheet(); }, 0);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet && !sheet.hidden) closeSheet();
    });

    /* The picker dispatches a bubbling change on the hidden id input for every
       variant update, including the reconcile snap. Signed-in only (guests get
       no form), hence the guard. */
    var hidden = buy.querySelector('[name="id"]');
    if (hidden) hidden.addEventListener('change', syncRows);

    syncRows();
  }

  /* ============================================================
     2. DETAIL SECTIONS — segmented tab strip
     ============================================================ */

  /* Sections are matched by their schema class (stable across every v2
     template) rather than by JSON key, because the ~99 per-handle
     product.<handle>-v2.json templates don't share key names. Two sections
     have no distinctive class of their own and are probed by content. */
  function byClass(c) { return $$('.shopify-section.' + c); }
  function probe(sel) {
    var el = $(sel);
    var sec = el && el.closest('.shopify-section');
    return sec ? [sec] : [];
  }

  function isEmpty(sec) {
    /* A v2 section renders nothing when its metafield is blank, but Shopify
       still emits the wrapper — so an empty wrapper means "no data". This is
       what keeps the tab strip honest: no metafield, no tab.

       Do NOT test children.length: every section also emits its own
       {{ 'x.css' | asset_url | stylesheet_tag }}, so a blank section still has
       one <link> child. Test for rendered content instead — text, or media in
       the sections that are purely visual. */
    if (!sec) return true;
    if (sec.textContent.trim().length) return false;
    return !sec.querySelector('img, picture, svg, video, iframe');
  }

  function buildTabs() {
    var reviewsHead = byClass('section-product-v2-reviews-head');
    var reviewApps = [];
    if (reviewsHead[0]) {
      var next = reviewsHead[0].nextElementSibling;
      if (next && next.classList.contains('shopify-section')) reviewApps = [next];
    }

    var TABS = [
      { key: 'overview', label: 'Overview', secs: [].concat(
          byClass('section-product-v2-tagline'),
          byClass('section-product-v2-why'),
          byClass('section-product-v2-benefits'),
          byClass('section-product-v2-in-use'),
          byClass('section-product-v2-case-study'),
          byClass('section-product-v2-related')) },
      { key: 'uses', label: "Where it's used", secs: byClass('section-product-v2-conditions') },
      { key: 'specs', label: 'Specs', secs: byClass('section-product-v2-details') },
      { key: 'install', label: 'Install', secs: byClass('section-product-v2-installation') },
      { key: 'compare', label: 'Compare', secs: [].concat(
          byClass('section-product-v2-size-guide'),
          byClass('section-product-v2-family'),
          probe('.cc-spec__summary, .cc-table')) },
      { key: 'reviews', label: 'Reviews', secs: [].concat(reviewsHead, reviewApps) }
    ];

    TABS.forEach(function (t) {
      t.secs = t.secs.filter(function (s) { return s && !isEmpty(s); });
    });
    var live = TABS.filter(function (t) { return t.secs.length; });
    if (live.length < 2) return;   // one tab is not a tab strip

    var strip = document.createElement('div');
    strip.className = 'pwatabs';
    var track = document.createElement('div');
    track.className = 'pwatabs__track';
    track.setAttribute('role', 'tablist');
    track.setAttribute('aria-label', 'Product detail');
    strip.appendChild(track);

    function show(key) {
      live.forEach(function (t) {
        var on = t.key === key;
        t.btn.setAttribute('aria-selected', on ? 'true' : 'false');
        t.secs.forEach(function (s) { s.classList.toggle('pwa-pdp-hide', !on); });
      });
    }

    live.forEach(function (t, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'pwatabs__tab';
      b.setAttribute('role', 'tab');
      b.textContent = t.label;
      b.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      b.addEventListener('click', function () {
        show(t.key);
        /* Keep the strip in view when switching from deep in a long tab. */
        var y = strip.getBoundingClientRect().top + window.scrollY - 96;
        if (window.scrollY > y) window.scrollTo({ top: y, behavior: 'smooth' });
      });
      t.btn = b;
      track.appendChild(b);
    });

    var mainSec = buy.closest('.shopify-section');
    if (!mainSec) return;
    mainSec.insertAdjacentElement('afterend', strip);

    /* The header's section wrapper is position:sticky; top:0 (z-index 50), so a
       strip stuck at top:0 hides underneath it. Park it directly below instead.
       Measured, not hardcoded: pwa.css restyles the header into a slimmer app
       bar, so its height differs from the public site's.

       Re-run on load. This theme async-loads stylesheets via the media="print"
       swap, so at deferred-script time the header's sticky rule may not have
       applied yet and we'd measure a static header and skip. */
    function parkStrip() {
      var hdr = document.querySelector('header.mti-v2-header');
      var hdrSec = hdr && hdr.closest('.shopify-section');
      if (!hdrSec || getComputedStyle(hdrSec).position !== 'sticky') return;
      var h = Math.round(hdrSec.getBoundingClientRect().height);
      if (h > 0 && h < 200) strip.style.top = h + 'px';
    }
    parkStrip();
    window.addEventListener('load', parkStrip);
    window.addEventListener('resize', parkStrip);

    show(live[0].key);
  }

  /* ============================================================
     3. CROSS-SELL — split the two rails by intent
     ============================================================ */

  /* "Frequently bought together" (complementary) is a cross-sell and belongs
     next to the buy decision. "Similar products" (related) are substitutes —
     an off-ramp beside Add to Cart — so they stay down in Overview. */
  function liftComplementary() {
    var comp = $('[data-pv2-rel-kind="complementary"]');
    if (!comp) return;
    var host = buy.querySelector('[data-pv2-buy-inner]') || buy;
    comp.classList.add('pwa-pdp-comp');
    host.appendChild(comp);
  }

  /* ============================================================
     4. GUEST — trade-pricing CTA opens the chooser sheet
     ============================================================ */

  /* "Get trade pricing" says what the shopper wants, not how they'll get there
     — and plenty of them already have an account. Dropping them straight onto
     a registration form makes an existing customer hunt for the login link. So
     in the app the CTA opens pwa-auth-sheet: create account, or sign in.

     Same shape as the bottom nav's Account item (pwa-install-banner.liquid:85):
     the href stays /pages/registration-form as the real destination — correct
     on the public site, and the fallback if this JS never runs — and
     data-auth-open intercepts it in the installed app. pwa-auth-sheet listens
     for [data-auth-open] at the document level and renders on every page.

     Scoped to the buy box and the sticky bar so we only ever catch this page's
     own CTAs — notably NOT the sheet's own "Create account" link, which points
     at the same URL and would otherwise re-open the sheet on top of itself. */
  function wireGuestCta() {
    [buy, document.querySelector('.pv2-stickybar')].forEach(function (scope) {
      if (!scope) return;
      $$('a[href="/pages/registration-form"]', scope).forEach(function (a) {
        a.setAttribute('data-auth-open', '');
      });
    });
  }

  buildOptions();
  buildTabs();
  liftComplementary();
  wireGuestCta();
})();
